import { useQuery } from '@tanstack/react-query';
import { useDataChainId } from '@ui/hooks/datachain';
import { getDexAggregator } from '@ui/services/contract/contract-instances/getDexAggregator';
import { Token } from '@ui/types/token';
import { Address, formatUnits, parseUnits } from 'viem';
import { usePublicClient, useAccount } from 'wagmi';

import { encodeAdapterData, getExchangePools } from '../config';

export interface ExactOutQuoteResult {
  bestCandidate: {
    adapter: Address;
    data: `0x${string}`;
  };
  amountIn: bigint;
  formattedAmountIn: string;
  // The amountOut returned for the chosen amountIn (useful for debugging/UX)
  amountOut: bigint;
  formattedAmountOut: string;
}

const MAX_DOUBLING_ITERS = 12;
const MAX_BINARY_SEARCH_ITERS = 20;

export const useExchangeQuoteExactOutput = ({
  inputToken,
  outputToken,
  outputAmount,
}: {
  inputToken?: Token;
  outputToken?: Token;
  outputAmount: string;
}) => {
  const chainId = useDataChainId();
  const publicClient = usePublicClient({ chainId });
  const { address } = useAccount();

  const isEnabled =
    !!inputToken?.address &&
    !!outputToken?.address &&
    !!outputAmount &&
    !!inputToken.decimals &&
    !!outputToken.decimals &&
    parseFloat(outputAmount) > 0 &&
    !!publicClient;

  return useQuery({
    queryKey: [
      'exchange-quote-exact-out',
      chainId,
      inputToken?.address,
      outputToken?.address,
      outputAmount,
      !!publicClient,
    ],
    enabled: isEnabled,
    refetchInterval: 10000,
    queryFn: async (): Promise<ExactOutQuoteResult | null> => {
      if (!publicClient) return null;
      if (!inputToken?.address || !outputToken?.address) return null;
      if (!inputToken.decimals || !outputToken.decimals) return null;

      const desiredOut = parseUnits(outputAmount, outputToken.decimals);
      if (desiredOut === 0n) return null;

      const dexAggregator = getDexAggregator({
        chainId,
        publicClient: publicClient as any,
      });

      const pools = getExchangePools(chainId, inputToken, outputToken);
      if (!pools || pools.length === 0) return null;

      const candidates = pools.map((pool) => ({
        adapter: pool.adapter,
        data: encodeAdapterData(pool, address),
      }));

      const quoteExactIn = async (amountIn: bigint) => {
        const [bestCandidate, bestAmountOut] =
          await dexAggregator.read.findBestPath([
            amountIn,
            inputToken.address as Address,
            outputToken.address as Address,
            candidates,
          ]);
        
return { bestCandidate, bestAmountOut };
      };

      // 1) Seed high bound using a 1-unit quote when possible.
      const oneIn = parseUnits('1', inputToken.decimals);
      let high = oneIn;
      let highQuote = await quoteExactIn(high);

      if (highQuote.bestAmountOut === 0n) return null;

      // Estimate high based on linear price from 1-unit quote, then add a 10% buffer.
      // high ≈ desiredOut / (outPerOneIn) * oneIn
      const outPerOneIn = highQuote.bestAmountOut;
      let estimatedHigh =
        (desiredOut * oneIn + (outPerOneIn - 1n)) / outPerOneIn; // ceil
      if (estimatedHigh < 1n) estimatedHigh = 1n;
      estimatedHigh = (estimatedHigh * 110n) / 100n + 1n; // +10% buffer

      high = estimatedHigh;
      highQuote = await quoteExactIn(high);

      // 2) If still not enough, exponentially increase high until it is.
      let doublingIters = 0;
      while (highQuote.bestAmountOut < desiredOut && doublingIters < MAX_DOUBLING_ITERS) {
        high = high * 2n;
        highQuote = await quoteExactIn(high);
        if (highQuote.bestAmountOut === 0n) return null;
        doublingIters += 1;
      }

      if (highQuote.bestAmountOut < desiredOut) return null;

      // 3) Binary search for the minimal amountIn that yields amountOut >= desiredOut.
      let low = 0n;
      let best = {
        amountIn: high,
        bestCandidate: highQuote.bestCandidate,
        amountOut: highQuote.bestAmountOut,
      };

      for (let i = 0; i < MAX_BINARY_SEARCH_ITERS; i++) {
        const mid = (low + high) / 2n;
        if (mid === low) break; // can't shrink further

        const midQuote = await quoteExactIn(mid);
        if (midQuote.bestAmountOut >= desiredOut && midQuote.bestAmountOut !== 0n) {
          best = {
            amountIn: mid,
            bestCandidate: midQuote.bestCandidate,
            amountOut: midQuote.bestAmountOut,
          };
          high = mid;
        } else {
          low = mid;
        }
      }

      return {
        bestCandidate: best.bestCandidate,
        amountIn: best.amountIn,
        formattedAmountIn: formatUnits(best.amountIn, inputToken.decimals),
        amountOut: best.amountOut,
        formattedAmountOut: formatUnits(best.amountOut, outputToken.decimals),
      };
    },
  });
};


